import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";

/**
 * Coordinates the `C` shortcut (in watch.tsx) with the comment composer, which
 * lives several layers down inside RightPanel. Rather than prop-drill a ref
 * through RightPanel → CommentsTab → CommentComposer, the composer registers a
 * focus callback here on mount and the page calls it.
 *
 * Consumed by BOTH the page-level key handler and the composer, so the provider
 * wraps WatchShell (see [[feedback-provider-at-layout-level]]). In guest/share
 * pages that don't mount the provider, useWatchShortcuts() returns null and the
 * composer simply skips registration.
 */
type FocusFn = () => void;

type WatchShortcutsCtx = {
  registerComposerFocus: (fn: FocusFn | null) => void;
  /** Focus the composer if one is mounted. Returns false if none registered. */
  focusComposer: () => boolean;
};

const Ctx = createContext<WatchShortcutsCtx | null>(null);

export function WatchShortcutsProvider({ children }: { children: ReactNode }) {
  const focusRef = useRef<FocusFn | null>(null);

  const registerComposerFocus = useCallback((fn: FocusFn | null) => {
    focusRef.current = fn;
  }, []);

  const focusComposer = useCallback(() => {
    if (focusRef.current) {
      focusRef.current();
      return true;
    }
    return false;
  }, []);

  const value = useMemo(
    () => ({ registerComposerFocus, focusComposer }),
    [registerComposerFocus, focusComposer],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWatchShortcuts(): WatchShortcutsCtx | null {
  return useContext(Ctx);
}
